import { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, Film, ChevronRight } from 'lucide-react';
import CountdownTimer from './sections/CountdownTimer';
import FeatureMiniCards from './sections/FeatureMiniCards';
import NotifyModal from './sections/NotifyModal';

const ComingSoon = ({ onBackToDashboard }) => {
  const [showModal, setShowModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Countdown Timer Logic
  useEffect(() => {
    const targetDate = new Date('2026-03-01T00:00:00').getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference < 0) {
        clearInterval(timer);
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-[70vh] flex flex-col relative font-sans rounded-[2rem] bg-white border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.02)]">
      {/* Main Content Wrapper (Blurs when modal is open) */}
      <div
        className={`flex-1 flex flex-col relative overflow-hidden rounded-[2rem] transition-all duration-500 ${
          showModal ? 'blur-md' : ''
        }`}
      >
        {/* Ambient Background Effects */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-teal-500/5 rounded-full mix-blend-multiply filter blur-[120px] animate-blob" />
          <div className="absolute top-[20%] right-[-10%] w-[400px] h-[400px] bg-emerald-500/5 rounded-full mix-blend-multiply filter blur-[120px] animate-blob animation-delay-2000" />
          <div className="absolute bottom-[-10%] left-[20%] w-[600px] h-[600px] bg-teal-600/5 rounded-full mix-blend-multiply filter blur-[120px] animate-blob animation-delay-4000" />
        </div>

        {/* Grid Pattern Overlay */}
        <div
          className="absolute inset-0 z-0 opacity-[0.01]"
          style={{
            backgroundImage:
              'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />

        <main className="flex-1 flex flex-col items-center justify-center px-6 relative z-10 py-12 sm:py-20 text-center">
          {/* Floating Icon */}
          <div
            className="mb-8 relative group cursor-pointer"
            onClick={() => setShowModal(true)}
          >
            <div className="absolute inset-0 bg-teal-500 rounded-full blur-2xl opacity-10 group-hover:opacity-20 transition-opacity duration-500" />
            <div className="relative bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-[0_15px_35px_rgba(0,0,0,0.05)] transform group-hover:scale-105 transition-transform duration-500 ease-out">
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-500/5 to-emerald-500/5 rounded-[2.5rem]" />
              <Film
                className="w-12 h-12 text-teal-600 drop-shadow-[0_0_10px_rgba(20,184,166,0.2)]"
                strokeWidth={1.5}
              />
            </div>
          </div>

          {/* Main Text Content */}
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-teal-50 border border-teal-100 text-teal-600 text-[10px] font-black tracking-[0.2em] animate-fade-in-up uppercase">
              <Sparkles size={14} />
              <span>Coming Soon to Trrip</span>
            </div>

            <h1 className="text-4xl md:text-7xl font-black tracking-tight text-teal-900 leading-[1.1] mb-2">
              Video{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600 animate-gradient-x">
                Itineraries
              </span>
              <div className="text-sm md:text-base font-black text-amber-500 mt-2 tracking-[0.3em] uppercase opacity-80">
                LAUNCHING SOON
              </div>
            </h1>

            <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto leading-relaxed font-medium">
              Engage clients with personalized cinematic reels. Showcase their journey before it begins.
            </p>
          </div>

          {/* Countdown Timer */}
          <CountdownTimer timeLeft={timeLeft} />

          {/* Feature Mini Cards */}
          <FeatureMiniCards />

          {/* Actions */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4 items-center">
            <button
              onClick={() => setShowModal(true)}
              className="px-10 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-2xl font-black shadow-lg shadow-teal-500/20 hover:shadow-teal-500/40 transition-all hover:-translate-y-1 active:scale-95 flex items-center gap-2 group tracking-wide cursor-pointer"
            >
              Notify Me
              <ChevronRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <button
              onClick={onBackToDashboard}
              className="px-8 py-4 bg-white hover:bg-gray-50 text-gray-600 rounded-2xl font-bold border border-gray-200 transition-all hover:-translate-y-1 active:scale-95 flex items-center gap-2 cursor-pointer"
            >
              <ArrowLeft size={18} />
              Dashboard
            </button>
          </div>
        </main>

        <footer className="py-6 text-center text-gray-400 text-[10px] border-t border-gray-50 bg-gray-50/20 font-black tracking-[0.2em] uppercase">
          <p>© 2026 Trrip Inc • Innovation in Progress</p>
        </footer>
      </div>

      {/* Success Modal */}
      <NotifyModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};

export default ComingSoon;
